import { writable } from 'svelte/store';

import { WEBUI_BASE_URL } from '$lib/constants';
import { isSensitiveActionUrl, measurementUrl } from './measurement-url';

/**
 * WHAT BROUGHT THIS VISITOR, REPORTED ONCE AND THEN LINKED TO THEIR ACCOUNT.
 *
 * WHY THE CLIENT REPORTS ANYTHING AT ALL. The first thing the server sees is an
 * authenticated or anonymous request for a page it already serves to everyone; which
 * ADVERT, which UTM values and which referring host the person arrived through exist
 * only in their address bar. Nothing server-side can reconstruct that.
 *
 * WHAT THE CLIENT IS *NOT* TRUSTED WITH. The steps that matter for money -- a paywall
 * refusal, a checkout, a purchase -- are recorded on the server, at the moment they
 * happen, because a browser can close the tab, lose the network, or simply not report.
 * This file reports the arrival and nothing else.
 *
 * THE VISITOR ID IS MINTED IN THE BROWSER AND KEPT THERE, because most visitors never
 * sign up and those are exactly the people a funnel has to count. It is a random
 * opaque value: it identifies a browser, not a person, and it is replaced whenever the
 * customer clears their storage.
 */
const VISITOR_KEY = 'frko_visitor';

const newVisitorId = (): string => {
	try {
		if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
	} catch {
		/* fall through to the manual form below */
	}
	return `v-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

export const visitorId = (): string => {
	try {
		const existing = localStorage.getItem(VISITOR_KEY);
		if (existing) return existing;
		const minted = newVisitorId();
		localStorage.setItem(VISITOR_KEY, minted);
		return minted;
	} catch {
		// Storage refused (private mode, a blocked cookie jar): report an identity that
		// lasts for this page only rather than not reporting at all.
		return newVisitorId();
	}
};

/** The attribution parameters we forward. Named to match `utils/funnel.py`. */
const ATTRIBUTION_KEYS = [
	'utm_source',
	'utm_medium',
	'utm_campaign',
	'utm_content',
	'utm_term'
] as const;

/**
 * Tell the server how this visitor arrived.
 *
 * CALLED TWICE ON PURPOSE, and both calls matter:
 *   * on load, anonymously -- this is the visit itself;
 *   * once a session exists -- this is what turns a visitor into a named account in
 *     the funnel, and it is the only moment that link can be made.
 *
 * Never throws and never blocks: a measurement that can break the page it measures is
 * worse than no measurement.
 */
export const reportVisit = async (): Promise<void> => {
	if (typeof window === 'undefined' || isSensitiveActionUrl(window.location.href)) return;

	try {
		const params = new URLSearchParams(window.location.search);
		const body: Record<string, string> = {
			visitor_id: visitorId(),
			path: window.location.pathname
		};

		for (const key of ATTRIBUTION_KEYS) {
			const value = params.get(key);
			if (value) body[key] = value;
		}

		// A `?from=` marker is how a link that cannot run JavaScript (the static
		// landing page) still says where it sent somebody from.
		const from = params.get('from');
		if (from) body.theme = from;

		if (document.referrer) body.referrer = measurementUrl(document.referrer);
		if (navigator.language) body.language = navigator.language;

		const headers: Record<string, string> = { 'Content-Type': 'application/json' };
		const token = localStorage.token;
		if (token) headers.Authorization = `Bearer ${token}`;

		await fetch(`${WEBUI_BASE_URL}/api/v1/funnel/visit`, {
			method: 'POST',
			headers,
			credentials: 'include',
			body: JSON.stringify(body)
		});
	} catch {
		/* the visit goes uncounted; the page is unaffected */
	}
};

/** The headers every funnel call needs, including the session when there is one. */
const post = async (path: string, body: Record<string, unknown>): Promise<any> => {
	const headers: Record<string, string> = { 'Content-Type': 'application/json' };
	try {
		const token = localStorage.token;
		if (token) headers.Authorization = `Bearer ${token}`;
	} catch {
		/* storage unavailable: report anonymously rather than not at all */
	}
	const response = await fetch(`${WEBUI_BASE_URL}${path}`, {
		method: 'POST',
		headers,
		credentials: 'include',
		body: JSON.stringify(body)
	});
	return response.ok ? await response.json() : null;
};

/**
 * WHICH SUGGESTIONS TO SHOW FIRST, ACCORDING TO WHAT HAS ACTUALLY WORKED.
 *
 * The words are ours (translated in `$lib/utils/suggestions`); the ORDER is the server's,
 * because the half of it that is measured -- impressions, clicks, and the accounts that
 * paid after clicking -- can only be known there. The server answers with ids.
 *
 * NEVER BLOCKS AND NEVER FAILS THE PAGE: if this call does not answer, the curated order
 * is already on screen and is a perfectly good list. Ranking is an improvement to the
 * page, not a precondition for it.
 */
export const fetchSuggestionOrder = async (): Promise<string[] | null> => {
	if (typeof window === 'undefined') return null;
	try {
		const params = new URLSearchParams(window.location.search);
		const from = params.get('from');
		const data = await post('/api/v1/funnel/suggestions', {
			visitor_id: visitorId(),
			path: window.location.pathname,
			theme: from || undefined,
			utm_campaign: params.get('utm_campaign') || undefined
		});
		const order = data?.order;
		return Array.isArray(order) && order.length ? order : null;
	} catch {
		return null;
	}
};

/**
 * THE DENOMINATOR. One event per rendered panel, carrying every id at once.
 *
 * Without it a click count cannot be interpreted: a suggestion is either genuinely
 * chosen or merely first on the screen, and only impressions tell the two apart. One row
 * per panel rather than one per chip, because the panel is rendered as a unit.
 */
export const reportSuggestionsShown = async (ids: string[]): Promise<void> => {
	if (typeof window === 'undefined' || !ids?.length) return;
	try {
		await post('/api/v1/funnel/suggestions/shown', { ids, visitor_id: visitorId() });
	} catch {
		/* unmeasured, not broken */
	}
};

/**
 * THE SIGNAL. Somebody chose this suggestion, which is the earliest evidence that it is
 * useful -- a purchase may be weeks away and an impression says nothing at all.
 *
 * Sent before the answer is requested and never awaited on the click path: a measurement
 * that delays the product is worse than a missing measurement.
 */
export const reportSuggestionClick = async (id: string): Promise<void> => {
	if (typeof window === 'undefined' || !id) return;
	try {
		await post('/api/v1/funnel/suggestions/click', { id, visitor_id: visitorId() });
	} catch {
		/* unmeasured, not broken */
	}
};

/**
 * THE ORDER THE SERVER DECIDED, AS A STORE BECAUSE TWO SURFACES READ IT.
 *
 * The app's placeholder and the pre-chat placeholder show the same list and must show it
 * in the same order; a module-level store is what keeps them from disagreeing. It starts
 * empty, which means "the curated order" -- the page is complete before the request is
 * even made.
 */
export const suggestionOrder = writable<string[]>([]);

/**
 * Ask the server for the order and hand it to the store. Called once per page load.
 *
 * Deliberately fire-and-forget: the caller does not await this to render.
 */
export const loadSuggestionOrder = async (): Promise<void> => {
	const order = await fetchSuggestionOrder();
	if (order) suggestionOrder.set(order);
};
